<template>
  <div>
    <router-link to="/admin" class="text-capoeira-600 font-semibold mb-4 inline-block">
      ← Volver a Admin
    </router-link>

    <h1 class="text-4xl font-bold mb-8">👨‍⚖️ Gestión de Jueces</h1>

    <!-- Formulario para agregar juez -->
    <div class="bg-white p-6 rounded-lg shadow mb-8">
      <h2 class="text-2xl font-bold mb-6 text-black">Agregar Nuevo Juez</h2>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label class="block text-sm font-semibold mb-2 text-black">Nombre Completo</label>
          <input
            v-model="formulario.nombre"
            type="text"
            placeholder="Ej: Juan Pérez"
            class="form-input"
          />
        </div>

        <div>
          <label class="block text-sm font-semibold mb-2">Usuario</label>
          <input
            v-model="formulario.usuario"
            type="text"
            placeholder="Ej: juez001"
            class="form-input"
          />
        </div>

        <div>
          <label class="block text-sm font-semibold mb-2">Contraseña</label>
          <input
            v-model="formulario.password"
            type="password"
            placeholder="••••••••"
            class="form-input"
          />
        </div>
      </div>

      <button
        @click="agregarJuez"
        :disabled="cargando"
        class="btn-primary"
      >
        {{ cargando ? '⏳ Agregando...' : '➕ Agregar Juez' }}
      </button>

      <Alert
        v-if="alerta.show"
        :type="alerta.type"
        :message="alerta.message"
        :duration="3000"
      />
    </div>

    <!-- Lista de jueces -->
    <div class="bg-white p-6 rounded-lg shadow">
      <h2 class="text-2xl font-bold mb-6 text-black">Jueces Registrados ({{ jueces.length }})</h2>

      <div v-if="jueces.length === 0" class="text-center py-12 text-gray-500">
        No hay jueces registrados aún
      </div>

      <table v-else class="w-full">
        <thead>
          <tr class="border-b-2 border-gray-300 text-black">
            <th class="text-left py-3 px-4">ID</th>
            <th class="text-left py-3 px-4">Nombre</th>
            <th class="text-left py-3 px-4">Usuario</th>
            <th class="text-center py-3 px-4">Estado</th>
            <th class="text-center py-3 px-4">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="juez in jueces" :key="juez.id" class="border-b border-gray-200 hover:bg-gray-50">
            <td class="py-3 px-4 font-bold text-capoeira-600">{{ juez.id }}</td>
            <td class="py-3 px-4 text-gray-600">{{ juez.nombre }}</td>
            <td class="py-3 px-4 text-gray-600">{{ juez.usuario }}</td>
            <td class="py-3 px-4 text-center">
              <span v-if="juez.activo" class="px-3 py-1 bg-green-200 text-green-800 rounded-full text-sm font-semibold">
                ✓ Activo
              </span>
              <span v-else class="px-3 py-1 bg-gray-200 text-gray-800 rounded-full text-sm font-semibold">
                ✕ Inactivo
              </span>
            </td>
            <td class="py-3 px-4 text-center">
              <button
                @click="eliminarJuez(juez)"
                :disabled="eliminandoId === juez.id"
                class="px-3 py-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded text-sm font-semibold"
              >
                {{ eliminandoId === juez.id ? '⏳...' : '🗑️ Eliminar' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
        <p class="text-sm text-blue-900">
          <strong>💡 Tip:</strong> Los jueces usarán su ID y usuario para acceder al panel de votación.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useAPI } from '../../composables/useAPI'
import Alert from '../../components/Alert.vue'

const api = useAPI()

const jueces = ref([])
const cargando = ref(false)
const eliminandoId = ref(null)

const formulario = ref({
  nombre: '',
  usuario: '',
  password: ''
})

const alerta = ref({
  show: false,
  type: 'info',
  message: ''
})

const agregarJuez = async () => {
  if (!formulario.value.nombre || !formulario.value.usuario || !formulario.value.password) {
    mostrarAlerta('error', 'Por favor completa todos los campos')
    return
  }

  try {
    cargando.value = true
    await api.apiCall('POST', '/jueces', formulario.value)
    mostrarAlerta('success', '✓ Juez agregado exitosamente')

    // Limpiar formulario y recargar lista
    formulario.value = { nombre: '', usuario: '', password: '' }
    await cargarJueces()
  } catch (err) {
    mostrarAlerta('error', err.message)
  } finally {
    cargando.value = false
  }
}

const eliminarJuez = async (juez) => {
  if (!confirm(`¿Eliminar al juez "${juez.nombre}"?`)) return

  try {
    eliminandoId.value = juez.id
    const resultado = await api.eliminarJuez(juez.id)

    if (resultado.desactivado) {
      mostrarAlerta('info', `El juez ya tiene votos registrados: se desactivó en lugar de eliminarse`)
    } else {
      mostrarAlerta('success', '✓ Juez eliminado exitosamente')
    }

    await cargarJueces()
  } catch (err) {
    mostrarAlerta('error', err.message)
  } finally {
    eliminandoId.value = null
  }
}

const cargarJueces = async () => {
  try {
    jueces.value = await api.getJueces()
  } catch (err) {
    mostrarAlerta('error', err.message)
  }
}

const mostrarAlerta = (type, message) => {
  alerta.value = { show: true, type, message }
}

onMounted(() => {
  cargarJueces()
})
</script>
